import React, { useState } from 'react'
import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import AnimatedContainer from '../../../../components/Animations/AnimatedContainer'

/**
 * Section-added counterpart to `ApplyDefaultQuestionsConflictModal` (CR-295).
 *
 * A separate component rather than a reuse, because the instructor version names instructors in
 * five places — the summary noun, the Instructor column, every row's `instructorName`, the row
 * key, and the force warning's "questions they have written themselves". Section-added has no
 * per-instructor dimension: one shared set per course section. Rendering a collapsed payload
 * through the instructor modal would report "3 instructors already have questions" when it means
 * three sections, and leave the Instructor column blank.
 *
 * The underlying decision was already section-level: the CR-270 follow-up made classification
 * all-or-none per section — a section with any touched item is left completely alone, only a
 * fully pristine section is refreshed. `isFullyPristine` carries that here unchanged. This modal
 * is that concept with the instructor rows collapsed away, not a new one.
 *
 * Spec: docs/specs/active/CR-295-section-added-questions/spec.md
 */
export interface ApplySectionDefaultQuestionsConflict {
  sectionId: string
  sectionTitle: string
  /**
   * Section code (`section.abbreviation` on the backend). Shown beside the title because a title
   * alone does not identify a section to an admin — several sections of one course share it.
   * Optional: a section row could carry a null abbreviation.
   */
  sectionAbbreviation?: string | null
  existingQuestionCount: number
  /** All-or-none per section: pristine → fully refreshed, touched → left completely alone. */
  isFullyPristine: boolean
  preservedQuestionCount: number
  replacedDefaultCount: number
  seededQuestionCount: number
  /** What `force: true` would additionally do, previewed under Advanced options. */
  forceReplacedCount: number
  forceSeededCount: number
}

export interface ApplySectionDefaultQuestionsConflictSummary {
  totalConflicts: number
  willBeKeptAsIsCount: number
  willBeRefreshedCount: number
  forceWouldReplaceCount: number
  forceWouldReplaceQuestionCount: number
}

interface ApplySectionDefaultQuestionsConflictModalProps {
  conflicts: ApplySectionDefaultQuestionsConflict[]
  summary: ApplySectionDefaultQuestionsConflictSummary
  onCancel: () => void
  onConfirm: () => void
  onForceConfirm: () => void
  isSubmitting?: boolean
}

const sectionNoun = (count: number): string => (count === 1 ? 'course section' : 'course sections')

export const ApplySectionDefaultQuestionsConflictModal: React.FC<ApplySectionDefaultQuestionsConflictModalProps> = ({
  conflicts,
  summary,
  onCancel,
  onConfirm,
  onForceConfirm,
  isSubmitting = false
}) => {
  const [showDetails, setShowDetails] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <>
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography fontWeight="bold" fontSize="0.875rem">
          {summary.totalConflicts} {sectionNoun(summary.totalConflicts)} already{' '}
          {summary.totalConflicts === 1 ? 'has' : 'have'} shared questions —{' '}
          {summary.willBeKeptAsIsCount} {summary.willBeKeptAsIsCount === 1 ? 'has' : 'have'} edits
          and will be kept as-is, {summary.willBeRefreshedCount}{' '}
          {summary.willBeRefreshedCount === 1 ? 'is' : 'are'} untouched and will be refreshed.
        </Typography>
      </Alert>

      <GenericButton
        label={showDetails ? 'Hide details' : 'Show details'}
        buttonType={ButtonType.Ghost}
        variant="text"
        onClick={() => { setShowDetails((prev) => !prev) }}
        sx={{ textDecoration: 'underline', mb: 2 }}
      />

      <AnimatedContainer isVisible={showDetails}>
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Course Section</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Existing Questions</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conflicts.map((conflict) => (
                <TableRow key={conflict.sectionId}>
                  <TableCell>
                    {conflict.sectionTitle}
                    {conflict.sectionAbbreviation != null && conflict.sectionAbbreviation !== '' && (
                      <Typography component="span" fontSize="13px" color="text.secondary" ml={1}>
                        {conflict.sectionAbbreviation}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{conflict.existingQuestionCount}</TableCell>
                  <TableCell>{conflict.isFullyPristine ? 'Will be refreshed' : 'Kept as-is'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </AnimatedContainer>

      <GenericButton
        label={showAdvanced ? 'Hide advanced options' : 'Advanced options'}
        buttonType={ButtonType.Ghost}
        variant="text"
        onClick={() => { setShowAdvanced((prev) => !prev) }}
        sx={{ textDecoration: 'underline', mb: 2 }}
      />

      <AnimatedContainer isVisible={showAdvanced}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography fontWeight="bold" fontSize="0.875rem">
            This will replace {summary.forceWouldReplaceQuestionCount} questions across{' '}
            {summary.forceWouldReplaceCount} {sectionNoun(summary.forceWouldReplaceCount)},
            including questions instructors have added to those sections themselves.
          </Typography>
        </Alert>
        <GenericButton
          label="Force Replace All"
          buttonType={ButtonType.Destructive}
          disabled={isSubmitting}
          onClick={onForceConfirm}
          sx={{ mb: 3 }}
        />
      </AnimatedContainer>

      <DefaultModalActions
        destructiveButtonProps={{ onClick: onCancel, disabled: isSubmitting }}
        confirmButtonProps={{
          onClick: onConfirm,
          text: 'Apply Template',
          buttonType: ButtonType.Primary,
          loading: isSubmitting
        }}
      />
    </>
  )
}
